import { z } from "zod";
import { observatorySchema } from "./features/observatory/schema";

const bucket = z.object({
  label: z.string().max(100),
  lower: z.number().int().min(20),
  upper: z.number().int().min(29),
});
export const reportingSnapshotSchema = z.object({
  contract: z.literal("helpdesk-work-records-v2"),
  minimum_tickets: z.number().int().min(20),
  dimensions: z.record(z.string(), z.array(bucket)),
});
export const reportingDateFilterSchema = z.object({
  available: z.boolean(),
  min_date: z.iso.date().nullable(),
  max_date: z.iso.date().nullable(),
  from_date: z.iso.date().nullable().optional(),
  to_date: z.iso.date().nullable().optional(),
});
export const reportingPeriodSchema = z.object({
  observatory: observatorySchema,
  date_filter: reportingDateFilterSchema,
});
export type ReportingDateFilter = z.infer<typeof reportingDateFilterSchema>;
export type ReportingPeriod = z.infer<typeof reportingPeriodSchema>;
export const reportingPreviewSchema = z.object({
  date_filter: reportingDateFilterSchema
    .extend({ index_digest: z.string().regex(/^[a-f0-9]{64}$/) })
    .optional(),
  observatory: observatorySchema.optional(),
  geography_review: z
    .object({
      minimum_tickets: z.number().int().min(20),
      coverage: z.record(z.string(), z.number().int().nonnegative()),
      regions: z.array(
        z.object({
          location: z.string(),
          label: z.string(),
          work_records: z.number().int().nonnegative(),
          distinct_tickets: z.number().int().nonnegative(),
          eligible_distinct_tickets: z.number().int().nonnegative(),
          status: z.enum([
            "released",
            "not_eligible",
            "below_minimum",
            "not_released",
          ]),
          published_range: z
            .object({ lower: z.number().int(), upper: z.number().int() })
            .nullable(),
        }),
      ),
    })
    .optional(),
  review: z.record(z.string(), z.number().int().nonnegative()),
  snapshot: reportingSnapshotSchema,
  preview_digest: z.string().regex(/^[a-f0-9]{64}$/),
});
const publicSchema = z.object({
  date_filter: reportingDateFilterSchema.nullable().optional(),
  observatory: observatorySchema.nullable().optional(),
  state: z.enum(["unpublished", "published", "withdrawn"]),
  snapshot: reportingSnapshotSchema.nullable(),
  published_at: z.string().nullable(),
  version: z.number().int().positive().nullable(),
});
export type PublicReporting = z.infer<typeof publicSchema>;
export type ReportingPreview = z.infer<typeof reportingPreviewSchema>;
export async function getPublicReporting(
  signal: AbortSignal,
  period?: { from_date: string; to_date: string; publication_version: number },
): Promise<PublicReporting> {
  const query = period
    ? `?${new URLSearchParams({ ...period, publication_version: String(period.publication_version) })}`
    : "";
  const response = await fetch(`/api/v1/public/threat-analytics${query}`, {
    signal,
    cache: "no-store",
  });
  if (!response.ok) {
    if (response.status === 429) throw new Error("REPORTING_RANGE_RATE_LIMIT");
    const body = (await response.json().catch(() => ({}))) as {
      detail?: unknown;
    };
    if (
      body.detail === "Reporting publication changed; refresh the public page"
    )
      throw new Error("REPORTING_PUBLICATION_CHANGED");
    throw new Error("Community reporting is unavailable.");
  }
  return publicSchema.parse(await response.json());
}
